import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, GraduationCap, RefreshCcw } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * EVC Zelfscan, in 5 minuten een inschatting welke startkwalificatie
 * via EVC Centrum VIGOR haalbaar is. NIET juridisch bindend, het echte
 * EVC-traject bepaalt de definitieve waardering.
 */

const vragen = [
  {
    id: "werkervaring",
    q: "Hoeveel jaar werkervaring heb je (betaald of onbetaald samen)?",
    opts: [
      { l: "Minder dan 1 jaar", v: 0 },
      { l: "1–3 jaar", v: 1 },
      { l: "3–6 jaar", v: 2 },
      { l: "Meer dan 6 jaar", v: 3 },
    ],
  },
  {
    id: "opleiding",
    q: "Wat is je hoogst afgeronde opleiding?",
    opts: [
      { l: "Geen diploma", v: 0 },
      { l: "VMBO / basisonderwijs", v: 1 },
      { l: "MBO-1 / entree-opleiding", v: 2 },
      { l: "MBO-2 of hoger", v: 3 },
    ],
  },
  {
    id: "domein",
    q: "In welk domein liggen je meeste ervaringen?",
    opts: [
      { l: "Zorg & welzijn", v: "zorg" },
      { l: "Techniek & bouw", v: "techniek" },
      { l: "Horeca / retail / dienstverlening", v: "diensten" },
      { l: "Administratie / kantoor", v: "admin" },
      { l: "Logistiek / transport", v: "logistiek" },
      { l: "Nog geen duidelijk domein", v: "onbepaald" },
    ],
  },
  {
    id: "vrijwilligerswerk",
    q: "Doe je vrijwilligerswerk of mantelzorg?",
    opts: [
      { l: "Nee", v: 0 },
      { l: "Ja, incidenteel", v: 1 },
      { l: "Ja, structureel (> 8u/week)", v: 2 },
    ],
  },
  {
    id: "certificaten",
    q: "Heb je losse certificaten (BHV, VCA, heftruck, taal, ICT)?",
    opts: [
      { l: "Nee", v: 0 },
      { l: "1 of 2", v: 1 },
      { l: "3 of meer", v: 2 },
    ],
  },
  {
    id: "motivatie",
    q: "Ben je bereid om 6–9 maanden aan een EVC-traject te werken?",
    opts: [
      { l: "Nu nog niet", v: 0 },
      { l: "Ik denk het wel", v: 1 },
      { l: "Ja, absoluut", v: 2 },
    ],
  },
];

function bepaalUitkomst(antwoorden) {
  const w = antwoorden.werkervaring ?? 0;
  const o = antwoorden.opleiding ?? 0;
  const v = antwoorden.vrijwilligerswerk ?? 0;
  const c = antwoorden.certificaten ?? 0;
  const m = antwoorden.motivatie ?? 0;
  const score = w * 2 + o + v + c + m;

  const domeinLabels = {
    zorg: "Zorg & Welzijn (bijv. Helpende Zorg & Welzijn)",
    techniek: "Techniek (bijv. Assistent Bouwen)",
    diensten: "Dienstverlening (bijv. Medewerker Facilitair / Horeca)",
    admin: "Administratie (bijv. Assistent Facilitair Medewerker)",
    logistiek: "Logistiek (bijv. Medewerker Logistiek)",
    onbepaald: "Nog te bepalen, de EVC-assessor helpt je hierin",
  };
  const domein = domeinLabels[antwoorden.domein] || domeinLabels.onbepaald;

  let niveau, kop, subtekst;
  if (m === 0) {
    niveau = "verkennen";
    kop = "Eerst verkennen, nog even geen EVC-traject";
    subtekst =
      "Motivatie is de belangrijkste factor. Bij LevelLeven begin je met START — 12 weken om weer grip te krijgen. Na START keer je terug naar deze scan.";
  } else if (score >= 10) {
    niveau = "MBO-2";
    kop = "Kansrijk voor een MBO-2 startkwalificatie via EVC";
    subtekst =
      "Op basis van je antwoorden lijkt een MBO-2 ervaringscertificaat via EVC Centrum VIGOR haalbaar binnen 6–9 maanden. De definitieve waardering ligt bij de EVC-assessor.";
  } else if (score >= 6) {
    niveau = "MBO-1 / MBO-2";
    kop = "Kansrijk voor MBO-1 of gedeeltelijk MBO-2 via EVC";
    subtekst =
      "Je bent goed op weg. Verwacht een MBO-1 (entree) ervaringscertificaat, met MBO-2 als vervolgroute na aanvullende ervaring of scholing.";
  } else {
    niveau = "opbouwen";
    kop = "Nog niet direct EVC, eerst ervaring opbouwen";
    subtekst =
      "LevelLeven START (12 weken) bouwt de ervaring en participatie op die een EVC-traject kansrijk maakt. Doe daarna deze scan opnieuw.";
  }

  return { niveau, kop, subtekst, domein, score };
}

export default function EvcZelfscan() {
  usePageMeta({
    title: "EVC-zelfscan, LevelLeven",
    description:
      "In vijf minuten een indicatie welke startkwalificatie via EVC Centrum VIGOR haalbaar is.",
  });
  const [step, setStep] = useState(0);
  const [antwoorden, setAntwoorden] = useState({});
  const isDone = step === vragen.length;

  const uitkomst = useMemo(() => (isDone ? bepaalUitkomst(antwoorden) : null), [isDone, antwoorden]);

  const kies = (id, v) => {
    setAntwoorden((s) => ({ ...s, [id]: v }));
    setStep((s) => s + 1);
  };
  const reset = () => {
    setAntwoorden({});
    setStep(0);
  };

  const huidig = vragen[step];
  const voortgangPct = ((step) / vragen.length) * 100;

  return (
    <div>
      <section className="py-16 sm:py-24">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <SectionLabel>EVC-zelfscan</SectionLabel>
          <h1 className="font-heading text-5xl sm:text-6xl leading-[1.03] tracking-tight text-[#1D2A22] font-light">
            Vijf minuten.{" "}
            <span className="italic text-[#E06B3F]">Een indicatie</span>.
          </h1>
          <p className="mt-8 text-lg text-[#4A5D4E] leading-relaxed font-light">
            Zes korte vragen. Aan het eind zie je welke startkwalificatie via
            EVC Centrum VIGOR waarschijnlijk haalbaar is. Geen bindende
            uitspraak, de assessor bepaalt de definitieve waardering.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <div className="rounded-3xl bg-[#FAF7F1] border border-[#E5E1D8] p-6 sm:p-10" data-testid="evc-zelfscan">
            {/* voortgangsbalk */}
            <div className="flex items-center gap-4 mb-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#7A8276]">
                {isDone ? "Klaar" : `Vraag ${step + 1} van ${vragen.length}`}
              </div>
              <div className="flex-1 h-1 bg-[#EFECE5] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#E06B3F]"
                  animate={{ width: `${isDone ? 100 : voortgangPct}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!isDone ? (
                <motion.div
                  key={huidig.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h2 className="font-heading text-2xl sm:text-3xl text-[#1D2A22] leading-snug font-light">
                    {huidig.q}
                  </h2>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {huidig.opts.map((o) => (
                      <button
                        key={o.l}
                        onClick={() => kies(huidig.id, o.v)}
                        data-testid={`evc-optie-${huidig.id}-${o.v}`}
                        className="text-left rounded-2xl border border-[#E5E1D8] hover:border-[#E06B3F] hover:bg-[#EFECE5] px-5 py-4 text-[#1D2A22] transition-colors"
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button
                      onClick={() => setStep((s) => s - 1)}
                      data-testid="evc-vorige"
                      className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7A8276] hover:text-[#1D2A22] transition-colors"
                    >
                      <ArrowLeft size={12} /> Vorige vraag
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="uitkomst"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="evc-uitkomst"
                >
                  <div className="flex items-start gap-4">
                    <GraduationCap size={28} className="text-[#E06B3F] shrink-0 mt-1" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] mb-2">
                        Jouw indicatie · {uitkomst.niveau}
                      </p>
                      <h2 className="font-heading text-3xl sm:text-4xl text-[#1D2A22] leading-tight font-light">
                        {uitkomst.kop}
                      </h2>
                      <p className="mt-4 text-[#4A5D4E] leading-relaxed">
                        {uitkomst.subtekst}
                      </p>
                      <div className="mt-6 rounded-2xl bg-[#EFECE5] border border-[#E5E1D8] p-5">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A8276]">
                          Mogelijke richting
                        </p>
                        <p className="mt-1 font-heading text-lg text-[#1D2A22]">
                          {uitkomst.domein}
                        </p>
                      </div>

                      <div className="mt-8 flex flex-wrap gap-3">
                        <Link
                          to="/contact?rol=deelnemer"
                          className="group inline-flex items-center gap-3 bg-[#1D2A22] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-6 pr-2 py-2 text-sm transition-colors"
                        >
                          <span>Vraag intake aan</span>
                          <span className="bg-[#E06B3F] group-hover:bg-[#FAF7F1] group-hover:text-[#1D2A22] text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                            <ArrowUpRight size={14} />
                          </span>
                        </Link>
                        <Link
                          to="/partners/evc-vigor"
                          className="inline-flex items-center gap-2 rounded-full border border-[#1D2A22] text-[#1D2A22] hover:bg-[#1D2A22] hover:text-[#FAF7F1] px-5 py-2 text-sm transition-colors"
                        >
                          Meer over VIGOR
                        </Link>
                        <button
                          onClick={reset}
                          data-testid="evc-reset"
                          className="inline-flex items-center gap-2 text-sm text-[#7A8276] hover:text-[#1D2A22] transition-colors"
                        >
                          <RefreshCcw size={13} /> Opnieuw
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="mt-10 pt-6 border-t border-[#E5E1D8] text-[11px] text-[#7A8276] italic leading-relaxed">
              Indicatief resultaat op basis van jouw antwoorden. Aan deze
              uitkomst kunnen geen rechten worden ontleend. De definitieve
              waardering vindt plaats door een geaccrediteerde EVC-assessor
              van EVC Centrum VIGOR volgens de landelijke standaard.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
